<template>
    <div class="field-designer">
        <div v-if="!canEdit" class="readonly-note" role="note">
            <span class="material-icons">visibility</span>
            Field definitions are read-only for your role.
        </div>

        <div class="field-list">
            <div v-for="(field, index) in fields" :key="field.key" class="field-row">
                <span class="field-drag material-icons">drag_indicator</span>
                <div class="field-copy">
                    <span class="field-key">
                        {{ displayKey(field) }}
                        <span v-if="field.required" class="required-dot" title="Required field">required</span>
                    </span>
                    <span class="field-label">{{ field.label }} · {{ field.type }}</span>
                </div>
                <div v-if="canEdit" class="field-actions">
                    <button
                        type="button"
                        class="mini-button"
                        :disabled="index === 0"
                        aria-label="Move up"
                        @click="move(index, -1)"
                    >
                        <span class="material-icons">arrow_upward</span>
                    </button>
                    <button
                        type="button"
                        class="mini-button"
                        :disabled="index === fields.length - 1"
                        aria-label="Move down"
                        @click="move(index, 1)"
                    >
                        <span class="material-icons">arrow_downward</span>
                    </button>
                    <button type="button" class="mini-button danger" aria-label="Remove field" @click="remove(index)">
                        <span class="material-icons">close</span>
                    </button>
                </div>
            </div>
            <div v-if="fields.length === 0" class="empty-fields">No personalization fields defined yet.</div>
        </div>

        <template v-if="canEdit">
            <div class="add-section">
                <label class="add-label">
                    <span>Add a system field</span>
                    <input
                        v-model="systemSearch"
                        type="search"
                        placeholder="Search system fields"
                        aria-label="Search system fields"
                    />
                    <select v-model="systemPick" @change="addSystemField">
                        <option value="">Choose a system field…</option>
                        <option v-for="item in availableSystemFields" :key="item.key" :value="item.key">
                            {{ item.label }} · {{ item.namespace }}
                        </option>
                    </select>
                </label>

                <div class="custom-form">
                    <span class="add-label">Add a custom field</span>
                    <div class="custom-grid">
                        <input v-model="custom.key" type="text" placeholder="key (e.g. promo_code)" maxlength="64" />
                        <input v-model="custom.label" type="text" placeholder="Label" maxlength="120" />
                        <select v-model="custom.type">
                            <option value="text">text</option>
                            <option value="url">url</option>
                            <option value="email">email</option>
                            <option value="number">number</option>
                            <option value="currency">currency</option>
                            <option value="date">date</option>
                            <option value="boolean">boolean</option>
                        </select>
                        <input v-model="custom.sampleValue" type="text" placeholder="Sample value (optional)" maxlength="255" />
                        <label class="required-toggle">
                            <input v-model="custom.required" type="checkbox" />
                            Required
                        </label>
                        <button type="button" class="add-button" :disabled="!custom.key || !custom.label" @click="addCustomField">
                            Add
                        </button>
                    </div>
                    <p v-if="customError" class="custom-error" role="alert">{{ customError }}</p>
                    <p class="hint">Custom keys must be lowercase, dot-free, and start with a letter (e.g. <code>promo_code</code>).</p>
                    <p class="privacy-hint" role="note">
                        Sample values are for preview and test emails only. Never paste real customer data here.
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: "FieldDesignerPanel",
    props: {
        fields: { type: Array, default: () => [] },
        catalog: { type: Array, default: () => [] },
        canEdit: { type: Boolean, default: false },
    },
    data() {
        return {
            systemPick: "",
            systemSearch: "",
            custom: { key: "", label: "", type: "text", sampleValue: "", required: false },
            customError: "",
        }
    },
    computed: {
        availableSystemFields() {
            const existing = new Set(this.fields.map((field) => field.key))
            const query = this.systemSearch.trim().toLowerCase()
            return this.catalog.filter((item) => {
                if (existing.has(item.key)) return false
                if (!query) return true
                return [item.key, item.label, item.namespace, item.description]
                    .filter(Boolean)
                    .some((value) => `${value}`.toLowerCase().includes(query))
            })
        },
    },
    methods: {
        displayKey(field) {
            return field.source === "custom" ? `{{${field.key}}}` : field.key
        },
        emitFields(next) {
            this.$emit("update", next)
        },
        move(index, delta) {
            const next = [...this.fields]
            const target = index + delta
            if (target < 0 || target >= next.length) return
            const [item] = next.splice(index, 1)
            next.splice(target, 0, item)
            this.emitFields(next)
        },
        remove(index) {
            const next = this.fields.filter((_field, i) => i !== index)
            this.emitFields(next)
        },
        addSystemField() {
            if (!this.systemPick) return
            const item = this.catalog.find((entry) => entry.key === this.systemPick)
            if (!item) return
            const next = [
                ...this.fields,
                {
                    key: item.key,
                    label: item.label,
                    type: item.type,
                    source: "system",
                    required: false,
                    sampleValue: "",
                },
            ]
            this.emitFields(next)
            this.systemPick = ""
            this.systemSearch = ""
        },
        addCustomField() {
            this.customError = ""
            const key = this.custom.key.trim()
            if (!/^[a-z][a-z0-9_]{0,63}$/.test(key)) {
                this.customError = "Custom field keys must be lowercase, start with a letter, and use only letters, numbers, and underscores."
                return
            }
            if (this.fields.some((field) => field.key === key)) {
                this.customError = "A field with that key already exists."
                return
            }
            const next = [
                ...this.fields,
                {
                    key,
                    label: this.custom.label.trim(),
                    type: this.custom.type,
                    source: "custom",
                    required: this.custom.required,
                    sampleValue: this.custom.sampleValue.trim() || null,
                },
            ]
            this.emitFields(next)
            this.custom = { key: "", label: "", type: "text", sampleValue: "", required: false }
        },
    },
}
</script>

<style lang="scss" scoped>
.field-designer {
    color: #173b35;
}

.readonly-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 9px 12px;
    border-left: 4px solid #74928a;
    background: #edf4f1;
    color: #45615a;
    font-size: 12px;
}

.field-list {
    display: grid;
    gap: 6px;
}

.field-row {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid #dde6e2;
    border-radius: 7px;
    background: #fbfdfc;
}

.field-drag {
    color: #a4b2ad;
    font-size: 18px;
}

.field-copy {
    display: grid;
    gap: 2px;
}

.field-key {
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 12px;
    font-weight: 700;
}

.required-dot {
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #f7e9d8;
    color: #8a5c24;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
}

.field-label {
    color: #6b7c76;
    font-size: 11px;
}

.field-actions {
    display: flex;
    gap: 4px;
}

.mini-button {
    display: grid;
    width: 26px;
    height: 26px;
    place-items: center;
    border: 1px solid #d8e2de;
    border-radius: 6px;
    background: #fff;
    color: #4f665e;
    cursor: pointer;
}

.mini-button:disabled {
    cursor: not-allowed;
    opacity: 0.4;
}

.mini-button.danger:hover {
    border-color: #e0b4ab;
    background: #fff6f4;
    color: #a34b3e;
}

.mini-button .material-icons {
    font-size: 15px;
}

.empty-fields {
    padding: 16px;
    border: 1px dashed #d4dfdb;
    border-radius: 7px;
    color: #7a8984;
    font-size: 12px;
    text-align: center;
}

.add-section {
    display: grid;
    gap: 14px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e4ebe8;
}

.add-label {
    display: grid;
    gap: 6px;
    color: #536961;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
}

.add-label select {
    min-height: 38px;
    padding: 0 10px;
    border: 1px solid #cbd7d3;
    border-radius: 6px;
    background: #fff;
    color: #173b35;
    font-size: 13px;
    text-transform: none;
}

.custom-form {
    display: grid;
    gap: 8px;
}

.custom-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 110px 1fr auto auto;
    gap: 8px;
    align-items: center;
}

.custom-grid input,
.custom-grid select {
    min-height: 38px;
    padding: 0 10px;
    border: 1px solid #cbd7d3;
    border-radius: 6px;
    background: #fff;
    color: #173b35;
    font-size: 13px;
}

.required-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #536961;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
}

.add-button {
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid #123b34;
    border-radius: 6px;
    background: #123b34;
    color: #fff;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
}

.add-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.custom-error {
    margin: 0;
    color: #a34b3e;
    font-size: 12px;
}

.hint {
    margin: 0;
    color: #7a8984;
    font-size: 11px;
}

.hint code {
    font-family: "SFMono-Regular", Consolas, monospace;
}

.privacy-hint {
    margin: 2px 0 0;
    padding: 8px 10px;
    border-left: 3px solid #b78343;
    background: #fbf4ea;
    color: #77552d;
    font-size: 11px;
    line-height: 1.4;
}
</style>
